import type { TFunction } from 'i18next'
import { SparklesIcon } from '@heroicons/react/24/solid'
import { CircularPlayButton, OnboardingStepStatus, type OnboardingStepCardV2Props } from '../components/cards'
import { AnswersImage } from '../components/AnswersImage'
import { TutorialImage } from '../components/TutorialImage'
import { INTERAX_COLORS } from '../../../constants/colors'
import { OnboardingChapter } from '../../../../../pure-js/enums/OnboardingChapter'
import { isTutorialOnboardingStepCompleted } from '../../../../../pure-js/libs/gameTutorialProgress'

export type OnboardingStepKey = 'intro' | 'assessment' | 'coach-goals' | 'game-intro' | 'tutorial'

/** Location-state flag the coach page sends back with "Continue onboarding" after goal-setting. */
export const CONTINUE_FROM_COACH_GOALS_STATE = 'continueFromCoachGoals'

export interface OnboardingStepContext {
  t: TFunction
  completion: Record<OnboardingStepKey, boolean>
  /** A leader's goal-setting step is about their leadership goals, so the card says so. */
  isLeadershipCoaching: boolean
  progress?: {
    assessment?: {
      answeredQuestions: number
    }
  }
  actions: {
    openIntroVideo: () => void
    openAssessmentModal: () => void
    openCoachGoals: () => void
    openGameVideo: () => void
    openTutorial: () => void
    startTutorial: () => void | Promise<void>
  }
}

export interface OnboardingStepDescriptor {
  key: OnboardingStepKey
  buildCardProps: (context: OnboardingStepContext) => OnboardingStepCardV2Props
}

const buildStatus = (
  completed: boolean,
  inProgress: boolean,
  labels: {
    completed: string
    inProgress?: string
    notCompleted: string
  }
): { status: OnboardingStepStatus; statusLabel: string } => {
  if (completed) {
    return { status: OnboardingStepStatus.COMPLETED, statusLabel: labels.completed }
  }

  if (inProgress) {
    return {
      status: OnboardingStepStatus.IN_PROGRESS,
      statusLabel: labels.inProgress ?? labels.notCompleted
    }
  }

  return { status: OnboardingStepStatus.NOT_COMPLETED, statusLabel: labels.notCompleted }
}

const introStep: OnboardingStepDescriptor = {
  key: 'intro',
  buildCardProps: ({ t, completion, actions }) => {
    const { status, statusLabel } = buildStatus(completion.intro, false, {
      completed: t('gamer.onboarding.steps.introductionVideo.statusCompleted'),
      notCompleted: t('gamer.onboarding.steps.introductionVideo.status')
    })

    return {
      stepNumber: 1,
      title: t('gamer.onboarding.steps.introductionVideo.title'),
      buttonText: t('gamer.onboarding.steps.introductionVideo.button'),
      status,
      statusLabel,
      icon: <CircularPlayButton size={120} onClick={actions.openIntroVideo} />,
      onAction: actions.openIntroVideo
    }
  }
}

const assessmentStep: OnboardingStepDescriptor = {
  key: 'assessment',
  buildCardProps: ({ t, completion, progress, actions }) => {
    const answeredQuestions = progress?.assessment?.answeredQuestions ?? 0
    const inProgress = !completion.assessment && answeredQuestions > 0
    const { status, statusLabel } = buildStatus(completion.assessment, inProgress, {
      completed: t('gamer.onboarding.steps.assessmentQuestions.statusCompleted'),
      inProgress: t('gamer.onboarding.steps.assessmentQuestions.statusInProgress'),
      notCompleted: t('gamer.onboarding.steps.assessmentQuestions.status')
    })

    return {
      stepNumber: 2,
      title: t('gamer.onboarding.steps.assessmentQuestions.title'),
      buttonText: t('gamer.onboarding.steps.assessmentQuestions.button'),
      status,
      statusLabel,
      icon: <AnswersImage size={240} />,
      onAction: actions.openAssessmentModal
    }
  }
}

const gameIntroStep: OnboardingStepDescriptor = {
  key: 'game-intro',
  buildCardProps: ({ t, completion, actions }) => {
    const { status, statusLabel } = buildStatus(completion['game-intro'], false, {
      completed: t('gamer.onboarding.steps.gameIntroduction.statusCompleted'),
      notCompleted: t('gamer.onboarding.steps.gameIntroduction.status')
    })

    return {
      stepNumber: 3,
      title: t('gamer.onboarding.steps.gameIntroduction.title'),
      buttonText: t('gamer.onboarding.steps.gameIntroduction.button'),
      status,
      statusLabel,
      icon: <CircularPlayButton size={120} onClick={actions.openGameVideo} />,
      onAction: actions.openGameVideo
    }
  }
}

const tutorialStep: OnboardingStepDescriptor = {
  key: 'tutorial',
  buildCardProps: ({ t, completion, actions }) => {
    const { status, statusLabel } = buildStatus(completion.tutorial, false, {
      completed: t('gamer.onboarding.steps.gameTutorial.statusCompleted'),
      notCompleted: t('gamer.onboarding.steps.gameTutorial.status')
    })

    return {
      stepNumber: 4,
      title: t('gamer.onboarding.steps.gameTutorial.title'),
      buttonText: t('gamer.onboarding.steps.gameTutorial.button'),
      status,
      statusLabel,
      icon: <TutorialImage size={120} />,
      onAction: actions.openTutorial,
      onButtonAction: actions.startTutorial
    }
  }
}

const coachGoalsStep: OnboardingStepDescriptor = {
  key: 'coach-goals',
  buildCardProps: ({ t, completion, isLeadershipCoaching, actions }) => {
    const { status, statusLabel } = buildStatus(completion['coach-goals'], false, {
      completed: t('gamer.onboarding.steps.coachGoals.statusCompleted'),
      notCompleted: t('gamer.onboarding.steps.coachGoals.status')
    })
    const title = isLeadershipCoaching
      ? t('gamer.onboarding.steps.coachGoals.leaderTitle')
      : t('gamer.onboarding.steps.coachGoals.title')

    return {
      stepNumber: 3,
      title,
      buttonText: t('gamer.onboarding.steps.coachGoals.button'),
      status,
      statusLabel,
      icon: <SparklesIcon width={110} color={INTERAX_COLORS.sun} />,
      onAction: actions.openCoachGoals
    }
  }
}

export const ONBOARDING_STEP_DESCRIPTORS: OnboardingStepDescriptor[] = [
  introStep,
  assessmentStep,
  gameIntroStep,
  tutorialStep
]

/**
 * The visible onboarding steps. The coach goal-setting step is inserted between step 2 and step 3 only
 * when the One-on-One Coach is enabled for the user's org. Display numbers are assigned by position so
 * there are never gaps regardless of whether the coach step is present.
 */
export const buildOnboardingStepDescriptors = (coachEnabled: boolean): OnboardingStepDescriptor[] => {
  const ordered = coachEnabled
    ? [introStep, assessmentStep, coachGoalsStep, gameIntroStep, tutorialStep]
    : [introStep, assessmentStep, gameIntroStep, tutorialStep]

  return ordered.map((descriptor, index) => ({
    ...descriptor,
    buildCardProps: (context) => ({ ...descriptor.buildCardProps(context), stepNumber: index + 1 })
  }))
}

/**
 * The chapters a participant has to finish. `Demographics` and `Assessment` are one card on screen but
 * two stored chapters, so completion is checked against the stored names rather than the cards.
 */
type OnboardingProgressUser = { completedOnboardingSteps?: string[]; tutorialCompletedAt?: string }

export const REQUIRED_ONBOARDING_CHAPTERS = [
  OnboardingChapter.Introduction,
  OnboardingChapter.Demographics,
  OnboardingChapter.Assessment,
  OnboardingChapter.GameIntroduction,
  OnboardingChapter.Tutorial
]

// Only the game can say the tutorial was played out, so that one chapter is not read from the stored list.
const hasCompletedChapter = (user: OnboardingProgressUser, chapter: OnboardingChapter): boolean => {
  if (chapter === OnboardingChapter.Tutorial) {
    return isTutorialOnboardingStepCompleted(user)
  }

  return (user.completedOnboardingSteps ?? []).includes(chapter)
}

/** Whether anything is still outstanding, so a prompt back to onboarding is worth showing. */
export const hasIncompleteOnboardingSteps = (user: OnboardingProgressUser = {}): boolean =>
  REQUIRED_ONBOARDING_CHAPTERS.some((chapter) => !hasCompletedChapter(user, chapter))
